import { ApiError, authedFetch } from '@/lib/api-client'

// Wire format de la API (ver /specs/api.md seccion "Noticias").
// Publicos: GET /noticias, GET /noticias/:id (skipAuth).
// Admin: POST / PATCH / DELETE / POST /:id/imagen.

export type EstadoNoticia = 'BORRADOR' | 'PUBLICADA'

export interface Noticia {
  id: string
  titulo: string
  descripcion: string
  imagenUrl: string | null
  ctaTexto: string
  ctaUrl: string
  featured: boolean
  estado: EstadoNoticia
  fechaPublicacion: string // YYYY-MM-DD
  createdAt: string
  updatedAt: string
}

export interface NoticiaListResponse {
  items: Noticia[]
  nextCursor: string | null
}

export interface CreateNoticiaBody {
  titulo: string
  descripcion: string
  ctaTexto: string
  ctaUrl: string
  fechaPublicacion: string
  featured: boolean
  estado: EstadoNoticia
}

export type UpdateNoticiaBody = Partial<CreateNoticiaBody>

interface ListNoticiasParams {
  limit?: number
  cursor?: string | null
  estado?: EstadoNoticia
}

// Listado para el admin: usa el endpoint /admin/noticias que ve borradores
// y acepta el filtro `estado`.
export async function listNoticias(
  params: ListNoticiasParams = {},
): Promise<NoticiaListResponse> {
  const search = new URLSearchParams()
  search.set('limit', String(params.limit ?? 50))
  if (params.cursor) search.set('cursor', params.cursor)
  if (params.estado) search.set('estado', params.estado)
  return authedFetch<NoticiaListResponse>(`/admin/noticias?${search.toString()}`, {
    method: 'GET',
  })
}

// Detalle: usa el endpoint publico GET /noticias/:id. Para borradores el admin
// lee el item completo desde el listado /admin/noticias y navega con esos datos
// — el backend devuelve 404 publico para borradores. Cuando navegamos directo
// a /dashboard/noticias/:id sin haber pasado por la lista (link directo o reload)
// y el item es borrador, usamos un fallback al listado admin filtrando por id.
export async function getNoticia(id: string): Promise<Noticia> {
  try {
    return await authedFetch<Noticia>(`/noticias/${id}`, {
      method: 'GET',
      skipAuth: true,
    })
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Buscar via admin endpoint (puede ser un borrador).
      const list = await authedFetch<NoticiaListResponse>(
        `/admin/noticias?limit=100`,
        { method: 'GET' },
      )
      const found = list.items.find((n) => n.id === id)
      if (found) return found
    }
    throw err
  }
}

export async function createNoticia(body: CreateNoticiaBody): Promise<Noticia> {
  return authedFetch<Noticia>('/noticias', { method: 'POST', body })
}

export async function updateNoticia(
  id: string,
  body: UpdateNoticiaBody,
): Promise<Noticia> {
  return authedFetch<Noticia>(`/noticias/${id}`, { method: 'PATCH', body })
}

export async function deleteNoticia(id: string): Promise<void> {
  await authedFetch<void>(`/noticias/${id}`, { method: 'DELETE' })
}

// Multipart upload: authedFetch fuerza Content-Type JSON, asi que para archivos
// usamos fetch directo contra el endpoint, extrayendo el token desde Firebase
// Auth (la misma fuente que alimenta setAuthToken en SessionProvider).
export async function uploadNoticiaImagen(
  id: string,
  file: File,
): Promise<Noticia> {
  const { getFirebaseAuth } = await import('@/features/auth/firebase')
  const baseUrl = getApiBaseUrl()
  const auth = getFirebaseAuth()
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null

  const form = new FormData()
  form.append('file', file)

  const headers = new Headers()
  headers.set('Accept', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(`${baseUrl}/noticias/${id}/imagen`, {
      method: 'POST',
      headers,
      body: form,
    })
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No pudimos conectarnos al servidor.')
  }

  const text = await res.text()
  const data: unknown = text ? safeParse(text) : null

  if (!res.ok) {
    const err = extractError(data)
    throw new ApiError(res.status, err.code, err.message, err.details)
  }

  return data as Noticia
}

function getApiBaseUrl(): string {
  const envBase = import.meta.env.VITE_API_BASE_URL as string | undefined
  if (!envBase) {
    throw new Error('VITE_API_BASE_URL no está configurada.')
  }
  return envBase.replace(/\/$/, '')
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

interface ErrShape {
  code: string
  message: string
  details?: unknown
}

function extractError(data: unknown): ErrShape {
  if (
    data &&
    typeof data === 'object' &&
    'error' in data &&
    (data as { error?: unknown }).error &&
    typeof (data as { error: unknown }).error === 'object'
  ) {
    const err = (data as { error: Record<string, unknown> }).error
    return {
      code: typeof err.code === 'string' ? err.code : 'UNKNOWN',
      message:
        typeof err.message === 'string'
          ? err.message
          : 'Ocurrio un error inesperado.',
      details: err.details,
    }
  }
  return { code: 'UNKNOWN', message: 'Ocurrio un error inesperado.' }
}
